/**
 * taxTheme.js — custom Blockly themes for the Tax Calculate block editor (S1-P7, Screen Layout D4).
 *
 * PURE ESM + HEADLESS-SAFE. Imports Blockly ONLY through the cross-env shim `./blocklyCore.js`
 * (NEVER `blockly/core` directly). `Blockly.Theme.defineTheme` is pure data — no DOM at load — so
 * the headless `.mjs` tests can import this module directly.
 *
 * ── What this module owns ────────────────────────────────────────────────────────────────────
 * The COLOUR/CHROME layer only. It supplies a `blockStyle` for EVERY style key the 2-A
 * block defs reference (`taxBlocks.js` → `TAX_BLOCK_STYLE_KEYS`) — miss one and those blocks render
 * grey. It also supplies the 10 `categoryStyles` the 2-C toolbox will name (contract below), the
 * canvas chrome (`componentStyles`) matched to the Vuetify surface per mode, and a Thai-capable
 * font. It does NOT build the toolbox (2-C) or inject the workspace (2-D).
 *
 * ── D4: themes are STATIC ────────────────────────────────────────────────────────────────────
 * Blockly themes cannot consume live CSS custom-properties, so there are TWO baked themes
 * (`taxThemeLight`, `taxThemeDark`, both `base: Blockly.Themes.Zelos`). The Vue view watches
 * Vuetify's `theme.global.name` and calls `ws.setTheme(pickTaxTheme(isDark))` on change.
 *
 * ── Colour source of truth = spec D4 ─────────────────────────────────────────────────────────
 * Hex below is the resolved Vuetify palette baked by spec `## Screen Layout` → Decision D4, and
 * cross-checked against the retiring `components/FlowComponents/Formula*Node.vue` accent map
 * (FormulaFieldNode SOURCE_MAP; FormulaBracketNode header `#7e57c2`). Each hex is commented with
 * the Vuetify token / accent it mirrors so a future palette change is traceable.
 *   Rule/rule-field → primary blue · Rule เหมา rate → cyan (split off rule blue: a rate is not an
 *   amount) · Personal → purple · Global → teal · Const → orange ·
 *   Output var + tax_define → indigo · Compare → a DISTINCT hue: pink (was teal on the old canvas —
 *   D4 splits it off Globals) · Operators + Number → blue-grey · Text literal → brown (S1-P9: a
 *   literal, but not a numeric one) · Functions → green · Bracket → its current header deep-purple ·
 *   Σ per-item loop + its item_* leaves → deep-orange (S1-P10; one hue for the pair on purpose).
 */
import Blockly from './blocklyCore.js'

// ── App font (index.html loads Kanit / Noto Sans Thai / Roboto) — Thai-capable stack. ──────────
const FONT_FAMILY = "'Kanit', 'Noto Sans Thai', 'Roboto', system-ui, sans-serif"

// ── Per-style block colours: [primary(fill), secondary(lighter field/shadow), tertiary(border)] ─
// Values are Material palette shades of each D4 hue. Zelos draws white block text on these
// saturated fills → legible in both modes. Dark = darker fill + lighter tertiary for edge contrast.
const BLOCK_COLOURS_LIGHT = {
  // Tax Rule / rule-field → Vuetify `primary` blue (≈ #1976D2)
  tax_rule_style: { colourPrimary: '#1976D2', colourSecondary: '#64B5F6', colourTertiary: '#0D47A1' },
  // Rule เหมา RATE → `cyan` (#00ACC1). A rate is NOT an amount: `income_40_5` and `income_40_5_rate`
  // are a symmetric pair, so cyan reads as related-to-blue yet is unmistakably not rule blue.
  tax_rate_style: { colourPrimary: '#00ACC1', colourSecondary: '#4DD0E1', colourTertiary: '#00838F' },
  // Personal Data → `purple` (#9C27B0)
  tax_personal_style: { colourPrimary: '#9C27B0', colourSecondary: '#CE93D8', colourTertiary: '#6A1B9A' },
  // Globals → `teal` (#009688)
  tax_global_style: { colourPrimary: '#009688', colourSecondary: '#4DB6AC', colourTertiary: '#00695C' },
  // Constant → `orange` (#FF9800)
  tax_const_style: { colourPrimary: '#FF9800', colourSecondary: '#FFB74D', colourTertiary: '#EF6C00' },
  // Output var + tax_define → `indigo` (#3F51B5)
  tax_output_style: { colourPrimary: '#3F51B5', colourSecondary: '#7986CB', colourTertiary: '#283593' },
  // Number → blue-grey (#607D8B)
  tax_number_style: { colourPrimary: '#607D8B', colourSecondary: '#90A4AE', colourTertiary: '#37474F' },
  // Text literal (tax_text) → `brown` (#6D4C41). A literal like Number, so NOT one of the catalog hues
  // — but pointedly not Number's blue-grey either: 'FEMALE' and 0 fit different sockets, and a shared
  // hue would promise otherwise. Brown is the last unused family here (orange = Const, pink = Compare).
  tax_text_style: { colourPrimary: '#6D4C41', colourSecondary: '#A1887F', colourTertiary: '#3E2723' },
  // Σ SUM(…) — the explicit boundary of an iterated computation (tax_func_sum) → deep-orange
  // (#E64A19). Deliberately NOT tax_func_style: MIN/MAX/ROUND/IF/IFS compute a value, while Σ changes
  // how many times everything inside it runs, and a block that changes the arity of the computation
  // must not look like one more function. Deep-orange stays apart from the two warm hues already in
  // use — Const's bright amber-orange (#FF9800) and FINAL's pure red (#E53935).
  tax_item_style: { colourPrimary: '#E64A19', colourSecondary: '#FF8A65', colourTertiary: '#BF360C' },
  // tax_path — deep indigo. Deliberately NOT the rule blue: a `[]` path is one value PER ITEM, and a
  // block that wore rule blue would read as the rule's single scalar value.
  tax_path_style: { colourPrimary: '#3949AB', colourSecondary: '#7986CB', colourTertiary: '#1A237E' },
  // tax_bool — muted slate. A TRUE/FALSE literal is a leaf like a number, but it is not a number, and
  // it must not look plugable where a 0 is.
  tax_bool_style: { colourPrimary: '#546E7A', colourSecondary: '#90A4AE', colourTertiary: '#263238' },
  // Operators (+ − × ÷) → blue-grey (#607D8B) — same family as Number
  tax_op_style: { colourPrimary: '#607D8B', colourSecondary: '#90A4AE', colourTertiary: '#37474F' },
  // Compare (< ≤ > ≥ = ≠) → DISTINCT hue: pink (#C2185B) — split off Globals/teal per D4
  tax_compare_style: { colourPrimary: '#C2185B', colourSecondary: '#F06292', colourTertiary: '#880E4F' },
  // Functions (MIN MAX ROUND IF) → green (#2E7D32)
  tax_func_style: { colourPrimary: '#2E7D32', colourSecondary: '#66BB6A', colourTertiary: '#1B5E20' },
  // Bracket → its current header deep-purple (#7E57C2, FormulaBracketNode)
  tax_bracket_style: { colourPrimary: '#7E57C2', colourSecondary: '#B39DDB', colourTertiary: '#512DA8' },
  // START (flow entry) → strong green (#43A047) — the "go" of the pipeline
  tax_start_style: { colourPrimary: '#43A047', colourSecondary: '#81C784', colourTertiary: '#2E7D32' },
  // FINAL / result (flow exit) → strong red (#E53935) — the "finish flag" where the value comes out
  tax_result_style: { colourPrimary: '#E53935', colourSecondary: '#EF9A9A', colourTertiary: '#B71C1C' },
}

const BLOCK_COLOURS_DARK = {
  // darker fills recede on the dark canvas; lighter tertiary keeps the block edge defined
  tax_rule_style: { colourPrimary: '#1565C0', colourSecondary: '#1E88E5', colourTertiary: '#64B5F6' },
  tax_rate_style: { colourPrimary: '#00838F', colourSecondary: '#0097A7', colourTertiary: '#4DD0E1' },
  tax_personal_style: { colourPrimary: '#7B1FA2', colourSecondary: '#9C27B0', colourTertiary: '#CE93D8' },
  tax_global_style: { colourPrimary: '#00796B', colourSecondary: '#00897B', colourTertiary: '#4DB6AC' },
  tax_const_style: { colourPrimary: '#EF6C00', colourSecondary: '#F57C00', colourTertiary: '#FFB74D' },
  tax_output_style: { colourPrimary: '#303F9F', colourSecondary: '#3949AB', colourTertiary: '#7986CB' },
  tax_number_style: { colourPrimary: '#455A64', colourSecondary: '#546E7A', colourTertiary: '#90A4AE' },
  tax_text_style: { colourPrimary: '#4E342E', colourSecondary: '#5D4037', colourTertiary: '#A1887F' },
  tax_item_style: { colourPrimary: '#BF360C', colourSecondary: '#D84315', colourTertiary: '#FF8A65' },
  tax_path_style: { colourPrimary: '#283593', colourSecondary: '#3949AB', colourTertiary: '#9FA8DA' },
  tax_bool_style: { colourPrimary: '#37474F', colourSecondary: '#455A64', colourTertiary: '#B0BEC5' },
  tax_op_style: { colourPrimary: '#455A64', colourSecondary: '#546E7A', colourTertiary: '#90A4AE' },
  tax_compare_style: { colourPrimary: '#AD1457', colourSecondary: '#C2185B', colourTertiary: '#F06292' },
  tax_func_style: { colourPrimary: '#1B5E20', colourSecondary: '#2E7D32', colourTertiary: '#66BB6A' },
  tax_bracket_style: { colourPrimary: '#512DA8', colourSecondary: '#5E35B1', colourTertiary: '#9575CD' },
  tax_start_style: { colourPrimary: '#2E7D32', colourSecondary: '#388E3C', colourTertiary: '#81C784' },
  tax_result_style: { colourPrimary: '#C62828', colourSecondary: '#D32F2F', colourTertiary: '#EF9A9A' },
}

// ── Toolbox category styles (contract for 2-C's buildToolbox: category name → { colour }). ─────
// Names mirror the toolbox order in the spec: Tax Rules · Personal · Constants · Bracket · Globals
// · Number · Operators · Compare · Functions · Output(define). Light = base accent; dark = a
// lightened accent so the category label reads on the dark toolbox.
const CATEGORY_COLOURS_LIGHT = {
  tax_rules_category: '#1976D2', // blue  (tax_rule_style)
  personal_category: '#9C27B0', // purple (tax_personal_style)
  constants_category: '#FF9800', // orange (tax_const_style)
  bracket_category: '#7E57C2', // deep-purple (tax_bracket_style)
  globals_category: '#009688', // teal   (tax_global_style)
  number_category: '#607D8B', // blue-grey (tax_number_style)
  operators_category: '#607D8B', // blue-grey (tax_op_style)
  compare_category: '#C2185B', // pink   (tax_compare_style)
  functions_category: '#2E7D32', // green  (tax_func_style)
  output_category: '#3F51B5', // indigo (tax_output_style / tax_define)
  paths_category: '#3949AB', // indigo-dark (tax_path_style)
}

const CATEGORY_COLOURS_DARK = {
  tax_rules_category: '#64B5F6',
  personal_category: '#CE93D8',
  constants_category: '#FFB74D',
  bracket_category: '#B39DDB',
  globals_category: '#4DB6AC',
  number_category: '#90A4AE',
  operators_category: '#90A4AE',
  compare_category: '#F06292',
  functions_category: '#81C784',
  paths_category: '#9FA8DA',
  output_category: '#7986CB',
}

// ── Canvas chrome (componentStyles) matched to the Vuetify surface/background per mode. ─────────
// Light ≈ Vuetify light (surface #FFFFFF); dark ≈ Vuetify dark (background #121212, surface #1E1E1E).
const COMPONENT_STYLES_LIGHT = {
  workspaceBackgroundColour: '#FFFFFF', // Vuetify light surface
  toolboxBackgroundColour: '#F5F5F5', // grey lighten-4 (toolbox rail)
  toolboxForegroundColour: '#1F1F1F',
  flyoutBackgroundColour: '#EEEEEE',
  flyoutForegroundColour: '#3D3D3D',
  flyoutOpacity: 0.95,
  scrollbarColour: '#BDBDBD',
  scrollbarOpacity: 0.6,
  insertionMarkerColour: '#1976D2', // primary blue
  insertionMarkerOpacity: 0.4,
  markerColour: '#1976D2',
  cursorColour: '#1976D2',
  selectedGlowColour: '#1976D2',
  selectedGlowOpacity: 0.4,
  gridColour: '#E0E0E0',
}

const COMPONENT_STYLES_DARK = {
  workspaceBackgroundColour: '#121212', // Vuetify dark background
  toolboxBackgroundColour: '#1E1E1E', // Vuetify dark surface
  toolboxForegroundColour: '#E0E0E0',
  flyoutBackgroundColour: '#252525',
  flyoutForegroundColour: '#CFCFCF',
  flyoutOpacity: 0.95,
  scrollbarColour: '#5A5A5A',
  scrollbarOpacity: 0.6,
  insertionMarkerColour: '#90CAF9', // light blue (readable on dark)
  insertionMarkerOpacity: 0.4,
  markerColour: '#90CAF9',
  cursorColour: '#90CAF9',
  selectedGlowColour: '#90CAF9',
  selectedGlowOpacity: 0.4,
  gridColour: '#2A2A2A',
}

const FONT_STYLE = { family: FONT_FAMILY, weight: '400', size: 12 }

/** categoryColours map → Blockly `categoryStyles` shape ({ name: { colour } }). */
function toCategoryStyles(colours) {
  const out = {}
  for (const key of Object.keys(colours)) out[key] = { colour: colours[key] }
  return out
}

// ── The two static themes (D4). `defineTheme` MERGES the Zelos base styles with ours (base block/
//    category styles remain; our 10 tax keys are added), so blocks that reference a tax style key
//    are covered and any stray base-typed block still renders. ────────────────────────────────
export const taxThemeLight = Blockly.Theme.defineTheme('taxLight', {
  base: Blockly.Themes.Zelos,
  blockStyles: BLOCK_COLOURS_LIGHT,
  categoryStyles: toCategoryStyles(CATEGORY_COLOURS_LIGHT),
  componentStyles: COMPONENT_STYLES_LIGHT,
  fontStyle: FONT_STYLE,
})

export const taxThemeDark = Blockly.Theme.defineTheme('taxDark', {
  base: Blockly.Themes.Zelos,
  blockStyles: BLOCK_COLOURS_DARK,
  categoryStyles: toCategoryStyles(CATEGORY_COLOURS_DARK),
  componentStyles: COMPONENT_STYLES_DARK,
  fontStyle: FONT_STYLE,
})

/**
 * Pick the theme matching the current Vuetify mode (the view calls `ws.setTheme(pickTaxTheme(...))`
 * when `theme.global.name` flips).
 * @param {boolean} isDark true when Vuetify is in dark mode
 * @returns {Blockly.Theme}
 */
export function pickTaxTheme(isDark) {
  return isDark ? taxThemeDark : taxThemeLight
}

export default { taxThemeLight, taxThemeDark, pickTaxTheme }
